import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  ListGroup,
  Badge,
  Button,
  Spinner,
  Alert,
  Tabs,
  Tab
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import attendanceService from '../services/attendanceService';
import NotificationService from '../services/notificationService';

const LearnerNotifications = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const cohortIds = Array.from(new Set([
    userProfile?.cohortId,
    ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
    ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
  ].filter(Boolean)));
  const isInactiveLearner = userProfile?.isActive === false ||
    ['inactive', 'dropoff', 'dropped'].includes(String(userProfile?.status || '').toLowerCase());
  const hasActiveCohort = !isInactiveLearner && cohortIds.length > 0;

  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [todos, setTodos] = useState([]);
  const [resources, setResources] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [acknowledgedItems, setAcknowledgedItems] = useState({});
  const [activeTab, setActiveTab] = useState('reminders');

  useEffect(() => {
    if (currentUser && userProfile?.role === 'learner' && hasActiveCohort) {
      loadAllData();
    } else {
      setReminders([]);
      setTodos([]);
      setResources([]);
      setLoading(false);
    }
  }, [currentUser, userProfile?.role, hasActiveCohort]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAcknowledgements(),
        loadReminders(),
        loadTodos(),
        loadResources(),
        loadUpdates()
      ]);
    } catch (error) {
      devError('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAcknowledgements = async () => {
    if (!currentUser) {
      setAcknowledgedItems({});
      return;
    }

    try {
      const snapshot = await getDoc(doc(db, 'users', currentUser.uid));
      const data = snapshot.data() || {};
      setAcknowledgedItems(data.dashboardAcknowledgements || {});
    } catch (error) {
      devError('Error loading dashboard acknowledgements:', error);
      setAcknowledgedItems({});
    }
  };

  const acknowledgeItem = async (itemId, itemType = 'dashboard') => {
    if (!currentUser || !itemId) return;

    const next = {
      ...acknowledgedItems,
      [itemId]: {
        acknowledgedAt: new Date().toISOString(),
        type: itemType
      }
    };

    setAcknowledgedItems(next);

    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        dashboardAcknowledgements: next
      }, { merge: true });
    } catch (error) {
      devError('Error saving acknowledgement:', error);
    }
  };

  // REMINDERS: Upcoming deadlines and events
  const loadReminders = async () => {
    if (!hasActiveCohort) {
      setReminders([]);
      return;
    }
    const remindersList = [];
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

    try {
      // 1. Upcoming Teams Meetings (next 3 days)
      const meetingsRef = collection(db, 'teamsMeetings');
      const meetingsQuery = query(
        meetingsRef,
        where('scheduledDateTime', '>=', Timestamp.fromDate(now)),
        where('scheduledDateTime', '<=', Timestamp.fromDate(threeDaysFromNow)),
        orderBy('scheduledDateTime', 'asc'),
        limit(5)
      );
      const meetingsSnapshot = await getDocs(meetingsQuery);
      meetingsSnapshot.forEach(doc => {
        const data = doc.data();
        const meetingCohortIds = Array.isArray(data.cohortIds)
          ? data.cohortIds
          : (data.cohortId ? [data.cohortId] : []);
        if (!cohortIds.length || !meetingCohortIds.some((id) => cohortIds.includes(id))) {
          return;
        }
        const meetingDate = data.scheduledDateTime?.toDate();
        remindersList.push({
          id: `meeting-${doc.id}`,
          type: 'meeting',
          title: data.title,
          description: `Teams Meeting: ${data.description || ''}`,
          dueDate: meetingDate,
          priority: getMeetingPriority(meetingDate),
          action: () => window.open(data.teamsLink, '_blank'),
          actionLabel: 'Join Meeting',
          icon: '📹'
        });
      });

      // 2. Assignment Deadlines (next 7 days)
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const assignmentsRef = collection(db, 'assignments');
      const assignmentsQuery = query(
        assignmentsRef,
        where('dueDate', '>=', Timestamp.fromDate(now)),
        where('dueDate', '<=', Timestamp.fromDate(sevenDaysFromNow)),
        orderBy('dueDate', 'asc'),
        limit(5)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);

      for (const docSnap of assignmentsSnapshot.docs) {
        const data = docSnap.data();
        const dueDate = data.dueDate?.toDate();

        // Check if learner has submitted
        const submissionsRef = collection(db, 'assignments', docSnap.id, 'submissions');
        const submissionQuery = query(
          submissionsRef,
          where('userId', '==', currentUser.uid)
        );
        const submissionSnapshot = await getDocs(submissionQuery);

        // Only add if not submitted or not graded
        if (submissionSnapshot.empty ||
            !['completed', 'graded'].includes(submissionSnapshot.docs[0]?.data()?.status)) {
          remindersList.push({
            id: `assignment-${docSnap.id}`,
            type: 'assignment',
            title: data.title,
            description: `Assignment due ${formatRelativeDate(dueDate)}`,
            dueDate: dueDate,
            priority: getDeadlinePriority(dueDate),
            action: () => navigate(`/assignments/${docSnap.id}`),
            actionLabel: 'View Assignment',
            icon: '📝'
          });
        }
      }

      // 3. Quiz Deadlines (next 7 days)
      const quizzesRef = collection(db, 'quizzes');
      const quizzesQuery = query(
        quizzesRef,
        where('dueDate', '>=', Timestamp.fromDate(now)),
        where('dueDate', '<=', Timestamp.fromDate(sevenDaysFromNow)),
        orderBy('dueDate', 'asc'),
        limit(5)
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);

      for (const docSnap of quizzesSnapshot.docs) {
        const data = docSnap.data();
        const isPublished = data.isPublished !== false && data.published !== false;
        if (!isPublished) {
          continue;
        }
        const dueDate = data.dueDate?.toDate();

        // Check if learner has completed
        const attemptsRef = collection(db, 'quizzes', docSnap.id, 'attempts');
        const attemptQuery = query(
          attemptsRef,
          where('userId', '==', currentUser.uid),
          where('status', '==', 'completed')
        );
        const attemptSnapshot = await getDocs(attemptQuery);

        // Only add if not completed
        if (attemptSnapshot.empty) {
          remindersList.push({
            id: `quiz-${docSnap.id}`,
            type: 'quiz',
            title: data.title,
            description: `Quiz due ${formatRelativeDate(dueDate)}`,
            dueDate: dueDate,
            priority: getDeadlinePriority(dueDate),
            action: () => navigate(`/quizzes/${docSnap.id}`),
            actionLabel: 'Take Quiz',
            icon: '🎯'
          });
        }
      }

      // 4. Document Submission Reminders (if incomplete)
      const getEffectiveDocumentStatus = (documentData = {}) => {
        if (documentData.reviewStatus === 'qa_approved') return 'verified';
        if (documentData.reviewStatus === 'qa_rejected') return 'rejected';
        if (documentData.status === 'approved') return 'verified';
        return documentData.status || 'pending';
      };

      const documentsRef = collection(db, 'users', currentUser.uid, 'documents');
      const documentsQuery = query(
        documentsRef,
        orderBy('uploadedAt', 'desc'),
        limit(10)
      );
      const documentsSnapshot = await getDocs(documentsQuery);
      documentsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.deleted === true) {
          return;
        }
        const effectiveStatus = getEffectiveDocumentStatus(data);
        if (effectiveStatus === 'rejected') {
          const feedback = data.rejectionReason || data.reviewNotes || 'Document rejected, needs resubmission';
          remindersList.push({
            id: `document-${doc.id}`,
            type: 'document',
            title: `${data.category || data.categoryName || data.documentType || 'Document'} - Action Required`,
            description: feedback,
            dueDate: null,
            priority: 'high',
            action: () => navigate('/documents'),
            actionLabel: 'Resubmit',
            icon: '📄'
          });
        }
      });

      // 5. Outstanding attendance registers with exact session dates
      const pendingAttendance = await attendanceService.getPendingSessionsForLearner(
        currentUser.uid,
        userProfile?.cohortId || null
      );
      const pendingSessions = Array.isArray(pendingAttendance?.sessions) ? pendingAttendance.sessions : [];
      if (pendingSessions.length > 0) {
        const sortedPending = pendingSessions
          .map((session) => ({
            ...session,
            scheduledDate: session?.scheduledDate ? new Date(session.scheduledDate) : null
          }))
          .sort((a, b) => {
            const aTime = a.scheduledDate ? a.scheduledDate.getTime() : Number.MAX_SAFE_INTEGER;
            const bTime = b.scheduledDate ? b.scheduledDate.getTime() : Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
          });

        const missingDates = sortedPending
          .map((session) => formatDateTime(session.scheduledDate))
          .filter(Boolean);

        remindersList.push({
          id: 'attendance-register-outstanding',
          type: 'attendance',
          title: 'Attendance Registers Outstanding',
          description: missingDates.length > 0
            ? `You still need to complete your attendance registers for: ${missingDates.join(', ')}.`
            : 'You still have attendance registers to complete.',
          dueDate: sortedPending[0]?.scheduledDate || null,
          priority: 'high',
          action: () => navigate('/attendance'),
          actionLabel: 'Complete Register',
          icon: '🗓️'
        });
      }

      // 6. Fillable Templates (from facilitators) - show as reminders
      const templatesRef = collection(db, 'resources');
      const templatesQuery = query(
        templatesRef,
        where('isFillableTemplate', '==', true),
        orderBy('createdAt', 'desc'),
        limit(12)
      );
      const templatesSnapshot = await getDocs(templatesQuery);
      for (const docSnap of templatesSnapshot.docs) {
        const data = docSnap.data();
        const accessLevel = data.accessLevel || 'public';
        if (['admin', 'facilitators'].includes(accessLevel)) {
          continue;
        }
        const templateCohortIds = Array.isArray(data.cohortIds)
          ? data.cohortIds
          : (data.cohortId ? [data.cohortId] : []);
        if (templateCohortIds.length > 0 && cohortIds.length && !templateCohortIds.some((id) => cohortIds.includes(id))) {
          continue;
        }
        if (templateCohortIds.length > 0 && !cohortIds.length) {
          continue;
        }

        const submissionsRef = collection(db, 'templateSubmissions');
        const submissionQuery = query(
          submissionsRef,
          where('templateId', '==', docSnap.id),
          where('userId', '==', currentUser.uid)
        );
        const submissionSnapshot = await getDocs(submissionQuery);

        if (submissionSnapshot.empty) {
          const dueDate = data.templateDueDate?.toDate ? data.templateDueDate.toDate() : null;
          remindersList.push({
            id: `template-reminder-${docSnap.id}`,
            type: 'template',
            title: data.title,
            description: data.templateInstructions || 'Fill and submit this template',
            dueDate,
            priority: getDeadlinePriority(dueDate),
            action: () => navigate('/resources'),
            actionLabel: 'Fill Template',
            icon: '📋'
          });
        }
      }

      // Sort by priority and due date
      remindersList.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        if (a.priority !== b.priority) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        if (a.dueDate && b.dueDate) {
          return a.dueDate - b.dueDate;
        }
        return 0;
      });

      const visibleReminders = remindersList
        .filter((item) => !acknowledgedItems[item.id])
        .slice(0, 10);

      setReminders(visibleReminders);
    } catch (error) {
      devError('Error loading reminders:', error);
    }
  };

  // TO-DOS: Pending tasks
  const loadTodos = async () => {
    if (!hasActiveCohort) {
      setTodos([]);
      return;
    }
    const todosList = [];
    const now = new Date();

    try {
      // 1. Pending Assignments
      const assignmentsRef = collection(db, 'assignments');
      const assignmentsQuery = query(
        assignmentsRef,
        where('dueDate', '>=', Timestamp.fromDate(now)),
        orderBy('dueDate', 'asc'),
        limit(10)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);

      for (const docSnap of assignmentsSnapshot.docs) {
        const data = docSnap.data();

        // Check submission status
        const submissionsRef = collection(db, 'assignments', docSnap.id, 'submissions');
        const submissionQuery = query(
          submissionsRef,
          where('userId', '==', currentUser.uid)
        );
        const submissionSnapshot = await getDocs(submissionQuery);

        const submission = submissionSnapshot.docs[0];
        const status = submission?.data()?.status || 'not_started';

        if (!['completed', 'graded'].includes(status)) {
          todosList.push({
            id: `assignment-${docSnap.id}`,
            type: 'assignment',
            title: data.title,
            description: data.description || 'Complete this assignment',
            dueDate: data.dueDate?.toDate(),
            status: status,
            progress: getStatusProgress(status),
            action: () => navigate(`/assignments/${docSnap.id}`),
            actionLabel: status === 'in_progress' ? 'Continue' : 'Start',
            icon: '📝'
          });
        }
      }

      // 2. Incomplete Quizzes
      const quizzesRef = collection(db, 'quizzes');
      const quizzesQuery = query(
        quizzesRef,
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);

      for (const docSnap of quizzesSnapshot.docs) {
        const data = docSnap.data();

        // Check if completed
        const attemptsRef = collection(db, 'quizzes', docSnap.id, 'attempts');
        const attemptQuery = query(
          attemptsRef,
          where('userId', '==', currentUser.uid),
          where('status', '==', 'completed')
        );
        const attemptSnapshot = await getDocs(attemptQuery);

        if (attemptSnapshot.empty) {
          todosList.push({
            id: `quiz-${docSnap.id}`,
            type: 'quiz',
            title: data.title,
            description: `${data.questions?.length || 0} questions`,
            dueDate: data.dueDate?.toDate(),
            status: 'not_started',
            progress: 0,
            action: () => navigate(`/quizzes/${docSnap.id}`),
            actionLabel: 'Start Quiz',
            icon: '🎯'
          });
        }
      }

      // 3. Fillable Templates
      const templatesRef = collection(db, 'resources');
      const templatesQuery = query(
        templatesRef,
        where('isFillableTemplate', '==', true),
        where('accessLevel', '==', 'learner'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const templatesSnapshot = await getDocs(templatesQuery);

      for (const docSnap of templatesSnapshot.docs) {
        const data = docSnap.data();

        // Check if template has been filled
        const submissionsRef = collection(db, 'templateSubmissions');
        const submissionQuery = query(
          submissionsRef,
          where('templateId', '==', docSnap.id),
          where('userId', '==', currentUser.uid)
        );
        const submissionSnapshot = await getDocs(submissionQuery);

        if (submissionSnapshot.empty) {
          todosList.push({
            id: `template-${docSnap.id}`,
            type: 'template',
            title: data.title,
            description: data.templateInstructions || 'Fill and submit this template',
            dueDate: data.templateDueDate?.toDate(),
            status: 'not_started',
            progress: 0,
            action: () => navigate('/resources'),
            actionLabel: 'Fill Template',
            icon: '📋'
          });
        }
      }

      // 4. Incomplete Modules
      if (userProfile?.enrolledPrograms) {
        const programsRef = collection(db, 'programs');
        const programQuery = query(
          programsRef,
          where('__name__', 'in', userProfile.enrolledPrograms.slice(0, 10))
        );
        const programSnapshot = await getDocs(programQuery);

        programSnapshot.forEach(doc => {
          const data = doc.data();
          const modules = data.modules || [];

          modules.forEach(module => {
            // Check progress from userProfile or learner progress collection
            const progressKey = `progress.${module.id}`;
            const moduleProgress = userProfile[progressKey] || 0;

            if (moduleProgress < 100) {
              todosList.push({
                id: `module-${module.id}`,
                type: 'module',
                title: module.moduleName || module.title,
                description: module.description || 'Complete this learning module',
                dueDate: null,
                status: moduleProgress > 0 ? 'in_progress' : 'not_started',
                progress: moduleProgress,
                action: () => navigate(`/modules/${module.id}`),
                actionLabel: moduleProgress > 0 ? 'Continue' : 'Start',
                icon: '📚'
              });
            }
          });
        });
      }

      // Sort by due date and status
      todosList.sort((a, b) => {
        // Overdue first
        if (a.dueDate && b.dueDate) {
          const aOverdue = a.dueDate < now;
          const bOverdue = b.dueDate < now;
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;
          return a.dueDate - b.dueDate;
        }
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;

        // In progress before not started
        const statusOrder = { in_progress: 0, not_started: 1 };
        return (statusOrder[a.status] || 1) - (statusOrder[b.status] || 1);
      });

      const visibleTodos = todosList
        .filter((item) => !acknowledgedItems[item.id])
        .slice(0, 12);

      setTodos(visibleTodos);
    } catch (error) {
      devError('Error loading todos:', error);
    }
  };

  const loadUpdates = async () => {
    if (!currentUser) {
      setUpdates([]);
      return;
    }

    try {
      const updatesQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(updatesQuery);
      const items = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || 'Update',
          message: data.message || '',
          type: data.type || 'general',
          read: data.read === true,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
          actionUrl: data.actionUrl || null
        };
      });
      setUpdates(items);
    } catch (error) {
      devError('Error loading updates:', error);
      setUpdates([]);
    }
  };

  const handleUpdateAcknowledge = async (update) => {
    try {
      await NotificationService.markAsRead(update.id);
      setUpdates((prev) => prev.map((item) => (
        item.id === update.id ? { ...item, read: true } : item
      )));
    } catch (error) {
      devError('Error acknowledging update:', error);
    }
  };

  // RESOURCES: Quick access to learning materials
  const loadResources = async () => {
    if (!hasActiveCohort) {
      setResources([]);
      return;
    }
    try {
      const resourcesList = [];

      // Get recently added resources
      const resourcesRef = collection(db, 'resources');
      const recentQuery = query(
        resourcesRef,
        orderBy('uploadedAt', 'desc'),
        limit(25)
      );
      const recentSnapshot = await getDocs(recentQuery);

      recentSnapshot.forEach(doc => {
        const data = doc.data();
        const accessLevel = data.accessLevel || 'public';
        if (accessLevel !== 'public') return;

        if (data.availableAt) {
          const availableAt = data.availableAt?.toDate?.() || new Date(data.availableAt);
          if (availableAt > new Date()) return;
        }

        const cohortIds = Array.isArray(data.cohortIds)
          ? data.cohortIds
          : (data.cohortId ? [data.cohortId] : []);
        if (!userProfile?.cohortId || cohortIds.length === 0) return;
        if (!cohortIds.includes(userProfile.cohortId)) return;
        if (userProfile?.programId && data.programId && data.programId !== userProfile.programId) return;

        resourcesList.push({
          id: doc.id,
          title: data.title,
          description: data.description || '',
          category: data.category,
          type: data.type,
          url: data.isExternalLink ? data.url : data.fileURL,
          isExternal: data.isExternalLink || false,
          createdAt: data.uploadedAt?.toDate(),
          icon: getResourceIcon(data.type, data.category)
        });
      });

      setResources(resourcesList.slice(0, 8));
    } catch (error) {
      devError('Error loading resources:', error);
    }
  };

  // Helper functions
  const getMeetingPriority = (meetingDate) => {
    const now = new Date();
    const hoursUntil = (meetingDate - now) / (1000 * 60 * 60);

    if (hoursUntil < 2) return 'high';
    if (hoursUntil < 24) return 'medium';
    return 'low';
  };

  const getDeadlinePriority = (dueDate) => {
    const now = new Date();
    const hoursUntil = (dueDate - now) / (1000 * 60 * 60);

    if (hoursUntil < 24) return 'high';
    if (hoursUntil < 72) return 'medium';
    return 'low';
  };

  const formatRelativeDate = (date) => {
    if (!date) return '';

    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return 'Overdue!';
    if (diffHours < 1) return 'in less than 1 hour';
    if (diffHours < 24) return `in ${diffHours} hours`;
    if (diffDays === 1) return 'tomorrow';
    if (diffDays < 7) return `in ${diffDays} days`;
    return date.toLocaleDateString();
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    return date.toLocaleString('en-ZA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusProgress = (status) => {
    const progressMap = {
      not_started: 0,
      in_progress: 50,
      submitted: 75,
      in_review: 80,
      completed: 100,
      graded: 100
    };
    return progressMap[status] || 0;
  };

  const getResourceIcon = (type, category) => {
    const typeIcons = {
      'video': '🎥',
      'document': '📄',
      'presentation': '📊',
      'link': '🔗',
      'pdf': '📕',
      'quiz': '🎯'
    };

    const categoryIcons = {
      'tutorial': '🎓',
      'reference': '📚',
      'example': '💡',
      'exercise': '✏️'
    };

    return typeIcons[type?.toLowerCase()] || categoryIcons[category?.toLowerCase()] || '📎';
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      high: 'danger',
      medium: 'warning',
      low: 'info'
    };
    return variants[priority] || 'secondary';
  };

  const getStatusBadge = (status) => {
    const variants = {
      not_started: 'secondary',
      in_progress: 'primary',
      submitted: 'info',
      in_review: 'warning',
      completed: 'success',
      graded: 'success'
    };
    return variants[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      submitted: 'Submitted',
      in_review: 'Under Review',
      completed: 'Completed',
      graded: 'Graded'
    };
    return labels[status] || status;
  };

  const summaryCards = [
    {
      id: 'summary-reminders',
      label: 'Urgent Reminders',
      value: reminders.length,
      variant: reminders.length > 0 ? 'danger' : 'success'
    },
    {
      id: 'summary-todos',
      label: 'Outstanding Tasks',
      value: todos.length,
      variant: todos.length > 0 ? 'warning' : 'success'
    },
    {
      id: 'summary-updates',
      label: 'Unread Updates',
      value: updates.filter((item) => !item.read).length,
      variant: updates.some((item) => !item.read) ? 'info' : 'success'
    }
  ];

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading notifications...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading your notifications...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-bell-fill me-2"></i>
            Your Dashboard
          </h5>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadAllData}
          >
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        {!hasActiveCohort ? (
          <Alert variant="light" className="mb-0">
            Your account is not currently assigned to a cohort. Reminders and resources will appear once you are assigned.
          </Alert>
        ) : (
        <>
        <Row className="mb-3">
          {summaryCards.map((item) => (
            <Col md={4} key={item.id} className="mb-3 mb-md-0">
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="text-center">
                  <div className={`fs-3 fw-bold text-${item.variant}`}>{item.value}</div>
                  <small className="text-muted">{item.label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <Tabs
          id="learner-notifications-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* REMINDERS TAB */}
          <Tab
            eventKey="reminders"
            title={
              <span>
                🔔 Reminders
                {reminders.length > 0 && (
                  <Badge bg="danger" pill className="ms-2">{reminders.length}</Badge>
                )}
              </span>
            }
          >
            {reminders.length > 0 ? (
              <ListGroup variant="flush">
                {reminders.map((reminder) => (
                  <ListGroup.Item
                    key={reminder.id}
                    className="px-0 border-start-0 border-end-0"
                  >
                    <div className="d-flex align-items-start">
                      <div className="me-3" style={{ fontSize: '1.5rem' }}>
                        {reminder.icon}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 className="mb-1">{reminder.title}</h6>
                          <Badge bg={getPriorityBadge(reminder.priority)} pill>
                            {reminder.priority}
                          </Badge>
                        </div>
                        <p className="text-muted small mb-2">{reminder.description}</p>
                        {reminder.dueDate && (
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            {formatDateTime(reminder.dueDate)}
                          </small>
                        )}
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        onClick={reminder.action}
                      >
                        {reminder.actionLabel}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ms-2"
                        onClick={() => acknowledgeItem(reminder.id, 'reminder')}
                      >
                        Acknowledge
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="success" className="mb-0">
                <i className="bi bi-check-circle me-2"></i>
                You're all caught up! No urgent reminders at the moment.
              </Alert>
            )}
          </Tab>

          {/* TO-DOS TAB */}
          <Tab
            eventKey="todos"
            title={
              <span>
                ✅ To-Do's
                {todos.length > 0 && (
                  <Badge bg="primary" pill className="ms-2">{todos.length}</Badge>
                )}
              </span>
            }
          >
            {todos.length > 0 ? (
              <ListGroup variant="flush">
                {todos.map((todo) => (
                  <ListGroup.Item
                    key={todo.id}
                    className="px-0 border-start-0 border-end-0"
                  >
                    <div className="d-flex align-items-start">
                      <div className="me-3" style={{ fontSize: '1.5rem' }}>
                        {todo.icon}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 className="mb-1">{todo.title}</h6>
                          <Badge bg={getStatusBadge(todo.status)}>
                            {getStatusLabel(todo.status)}
                          </Badge>
                        </div>
                        <p className="text-muted small mb-2">{todo.description}</p>

                        {/* Progress bar for in-progress items */}
                        {todo.progress > 0 && (
                          <div className="mb-2">
                            <div className="progress" style={{ height: '6px' }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${todo.progress}%` }}
                                aria-valuenow={todo.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                            <small className="text-muted">{todo.progress}% complete</small>
                          </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center">
                          {todo.dueDate && (
                            <small className={`${todo.dueDate < new Date() ? 'text-danger fw-bold' : 'text-muted'}`}>
                              <i className="bi bi-calendar-event me-1"></i>
                              {todo.dueDate < new Date() ? 'Overdue!' : `Due ${formatRelativeDate(todo.dueDate)}`}
                            </small>
                          )}
                          <Button
                            variant={todo.status === 'in_progress' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={todo.action}
                          >
                            {todo.actionLabel}
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2"
                            onClick={() => acknowledgeItem(todo.id, 'todo')}
                          >
                            Mark Acknowledged
                          </Button>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="success" className="mb-0">
                <i className="bi bi-check-circle me-2"></i>
                Great job! You've completed all your tasks.
              </Alert>
            )}
          </Tab>

          {/* RESOURCES TAB */}
          <Tab
            eventKey="resources"
            title={
              <span>
                📚 Resources
                {resources.length > 0 && (
                  <Badge bg="info" pill className="ms-2">{resources.length}</Badge>
                )}
              </span>
            }
          >
            {resources.length > 0 ? (
              <>
                <Row className="mb-3">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Recently added learning materials</small>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate('/resources')}
                      >
                        View All Resources <i className="bi bi-arrow-right"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  {resources.map((resource) => (
                    <Col md={6} key={resource.id} className="mb-3">
                      <Card className="h-100 border">
                        <Card.Body>
                          <div className="d-flex align-items-start">
                            <div className="me-3" style={{ fontSize: '2rem' }}>
                              {resource.icon}
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1">{resource.title}</h6>
                              <p className="text-muted small mb-2">
                                {resource.description.length > 80
                                  ? `${resource.description.substring(0, 80)}...`
                                  : resource.description}
                              </p>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <Badge bg="secondary" className="me-1">{resource.category}</Badge>
                                  <Badge bg="light" text="dark">{resource.type}</Badge>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  href={resource.url}
                                  target={resource.isExternal ? '_blank' : '_self'}
                                  rel={resource.isExternal ? 'noopener noreferrer' : ''}
                                >
                                  {resource.isExternal ? (
                                    <>Open <i className="bi bi-box-arrow-up-right ms-1"></i></>
                                  ) : (
                                    <>View <i className="bi bi-eye ms-1"></i></>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            ) : (
              <Alert variant="info" className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                No resources available yet. Check back later!
              </Alert>
            )}
          </Tab>

          <Tab
            eventKey="updates"
            title={
              <span>
                📨 Updates
                {updates.some((item) => !item.read) && (
                  <Badge bg="info" pill className="ms-2">{updates.filter((item) => !item.read).length}</Badge>
                )}
              </span>
            }
          >
            {updates.length > 0 ? (
              <ListGroup variant="flush">
                {updates.map((update) => (
                  <ListGroup.Item key={update.id} className="px-0 border-start-0 border-end-0">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h6 className="mb-0">{update.title}</h6>
                          {!update.read && <Badge bg="info">New</Badge>}
                        </div>
                        <div className="text-muted small mb-1">{update.message}</div>
                        <small className="text-muted">
                          {update.createdAt ? formatDateTime(update.createdAt) : 'Recent'}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        {update.actionUrl && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(update.actionUrl)}
                          >
                            Open
                          </Button>
                        )}
                        {!update.read && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleUpdateAcknowledge(update)}
                          >
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="light" className="mb-0">
                No recent updates yet.
              </Alert>
            )}
          </Tab>
        </Tabs>
        </>
        )}
      </Card.Body>
    </Card>
  );
};

export default LearnerNotifications;
